/**
 * Export der IDS-Anforderungen als Revit-Shared-Parameter-Datei (.txt).
 *
 * <h2>Format</h2>
 * Bewusst das ältere, breit kompatible `VERSION 2`-Format (GUID/NAME/DATATYPE/DATACATEGORY/GROUP/
 * VISIBLE/DESCRIPTION/USERMODIFIABLE als feste Spalten mit klassischen Token-Datentypen wie `TEXT`,
 * `LENGTH`, `YESNO`) statt der neueren unit-aware `autodesk.spec.aec:*`-Typkennungen — genau dieses
 * Format nutzt Autodesk selbst noch heute im offiziellen `revit-ifc`-Werkzeug, und es wird von allen
 * gängigen Revit-Versionen gelesen.
 *
 * <h2>GUID-Kontinuität</h2>
 * Wird eine bestehende Datei mitgegeben, bekommen Parameter mit demselben Namen (case-insensitiv)
 * dieselbe GUID wie zuvor — sonst müssten Modelle nach jedem Re-Export neu gemappt werden. Parameter
 * aus der Altdatei, die im aktuellen IDS nicht mehr vorkommen, bleiben standardmäßig erhalten (siehe
 * {@link RevitExportOptions.keepOrphanedParams}), damit andere Familien, die sie nutzen, nicht
 * brechen.
 */

import {IDSFacet, IDSRule, IDSValueField} from './ids.model';
import {dataTypeGroupOf, primitiveOfDataType} from './ifc-data-type-groups';

/** Von diesem Export unterstützte, verifizierte Revit-DATATYPE-Token. */
export const REVIT_DATA_TYPES = ['TEXT', 'INTEGER', 'NUMBER', 'LENGTH', 'AREA', 'VOLUME', 'ANGLE', 'YESNO', 'URL'] as const;
export type RevitDataType = typeof REVIT_DATA_TYPES[number];

/** Fallback-Auswahl für den Editor: nur die vier sinnvollen "ich weiß es nicht"-Typen. */
export const REVIT_FALLBACK_DATA_TYPES: RevitDataType[] = ['TEXT', 'NUMBER', 'INTEGER', 'YESNO'];

export interface RevitExportOptions {
  /** Wonach Parameter zu `GROUP`-Einträgen zusammengefasst werden. `none` = eine einzige Gruppe. */
  groupBy: 'specification' | 'propertySet' | 'none';
  /** Parametername = nur die Eigenschaft, oder `PropertySet.Eigenschaft` (vermeidet Namenskollisionen). */
  nameScheme: 'property' | 'propertySetProperty';
  /** DATATYPE für Eigenschaften ohne (erkennbaren) Datentyp. */
  fallbackDataType: RevitDataType;
  /** Parameter aus einer hochgeladenen Altdatei behalten, die im aktuellen IDS nicht mehr vorkommen. */
  keepOrphanedParams: boolean;
  /**
   * Eigenschaften, die nicht exportiert werden (#2498). Verglichen wird der Eigenschaftsname aus
   * der Anforderung - also vor dem `nameScheme` - case-insensitiv; ein `*` am Ende wirkt als
   * Präfix-Filter ("BIC_*"). Siehe {@link parseExclusionList} zum Einlesen der Eingabefelder.
   */
  excludedProperties: string[];
  /** PropertySets, deren Eigenschaften nicht exportiert werden - gleiche Vergleichsregeln. */
  excludedPropertySets: string[];
}

/**
 * Liest ein Filter-Eingabefeld ("Pset_Allgemein; BIC_*") als Liste. Trennzeichen sind Komma,
 * Semikolon und Zeilenumbruch, damit eine aus Excel kopierte Spalte direkt funktioniert.
 */
export function parseExclusionList(raw: string): string[] {
  return raw.split(/[,;\r\n]/).map(entry => entry.trim()).filter(Boolean);
}

/** Case-insensitiver Abgleich gegen eine Ausschlussliste, mit `*` am Ende als Präfix-Filter. */
function isExcluded(value: string | null, patterns: readonly string[]): boolean {
  if (!value || patterns.length === 0) return false;
  const name = value.trim().toLowerCase();
  return patterns.some(pattern => {
    const p = pattern.trim().toLowerCase();
    if (!p) return false;
    return p.endsWith('*') ? name.startsWith(p.slice(0, -1)) : name === p;
  });
}

export interface ExistingSpGroup {
  id: number;
  name: string;
}

export interface ExistingSpParam {
  guid: string;
  name: string;
  /** Roher DATATYPE-Wert der Datei - bei übernommenen Altparametern nicht auf {@link RevitDataType} beschränkt. */
  dataType: string;
  dataCategory: string;
  groupId: number;
  visible: boolean;
  description: string;
  userModifiable: boolean;
}

export interface ExistingSharedParameterFile {
  groups: ExistingSpGroup[];
  params: ExistingSpParam[];
}

export interface RevitExportResult {
  fileText: string;
  paramCount: number;
  groupCount: number;
  /** Eigenschaftsnamen, die mehrfach aufgelöst wurden - nur die erste Zuordnung wurde übernommen. */
  skippedDuplicateNames: string[];
  /** Anforderungsfacetten ohne literalen (Muster-/Bereichs-)Eigenschaftsnamen - übersprungen. */
  skippedInvalidFacets: number;
  /** Aus der Altdatei unverändert übernommene, im aktuellen IDS nicht mehr vorkommende Parameter. */
  orphanedKept: number;
  /** Durch die Ausschlusslisten übersprungene Eigenschafts-Anforderungen (#2498). */
  skippedByFilter: number;
}

/**
 * Liest eine bestehende Shared-Parameter-Datei spaltenbasiert über die `*PARAM`-Kopfzeile ein statt
 * über feste Spaltenpositionen - toleriert so z.B. Dateien mit zusätzlichen/fehlenden Spalten,
 * solange GUID/NAME/DATATYPE/GROUP vorhanden sind.
 */
export function parseSharedParameterFile(text: string): ExistingSharedParameterFile {
  const groups: ExistingSpGroup[] = [];
  const params: ExistingSpParam[] = [];
  let paramColumns: string[] | null = null;

  for (const rawLine of text.split(/\r\n|\r|\n/)) {
    if (!rawLine.trim()) continue;
    const cols = rawLine.split('\t');

    if (cols[0] === '*PARAM') {
      paramColumns = cols;
      continue;
    }
    if (cols[0] === 'GROUP') {
      const id = Number(cols[1]);
      if (Number.isFinite(id)) groups.push({id, name: cols[2] ?? ''});
      continue;
    }
    if (cols[0] === 'PARAM' && paramColumns) {
      const at = (name: string): string => {
        const index = paramColumns!.indexOf(name);
        return index >= 0 ? (cols[index] ?? '') : '';
      };
      const name = at('NAME');
      if (!name) continue;
      const groupId = Number(at('GROUP'));
      params.push({
        guid: at('GUID') || crypto.randomUUID(),
        name,
        dataType: at('DATATYPE') || 'TEXT',
        dataCategory: at('DATACATEGORY'),
        groupId: Number.isFinite(groupId) ? groupId : 0,
        visible: at('VISIBLE') !== '0',
        description: at('DESCRIPTION'),
        userModifiable: at('USERMODIFIABLE') !== '0',
      });
    }
  }

  return {groups, params};
}

/** BIC-Datentypengruppen (siehe `ifc-data-type-groups.ts`) auf den passenden Revit-Typ. */
const BIC_GROUP_TO_REVIT: Record<string, RevitDataType> = {
  TEXT: 'TEXT',
  ZAHL: 'NUMBER',
  GANZZAHL: 'INTEGER',
  WAHRHEITSWERT: 'YESNO',
};

/**
 * Feinere Zuordnung für bekannte IFC-Maßtypen, bevor auf den EXPRESS-Grundtyp zurückgefallen wird -
 * `IfcLengthMeasure` etc. sollen als `LENGTH` und nicht nur als `NUMBER` ankommen. Bewusst nur die
 * vier Revit-Einheitentypen, die im legacy-Format sicher belegt sind (siehe Modul-Kommentar); für
 * alles andere (Masse, Währung, Kraft, ...) greift stattdessen der EXPRESS-Grundtyp, um keine
 * unbestätigten Token-Namen zu raten.
 */
const MEASURE_NAME_OVERRIDES: { pattern: RegExp; dataType: RevitDataType }[] = [
  {pattern: /length/i, dataType: 'LENGTH'},
  {pattern: /area/i, dataType: 'AREA'},
  {pattern: /volume/i, dataType: 'VOLUME'},
  {pattern: /planeangle|solidangle/i, dataType: 'ANGLE'},
];

/** Ordnet einen IDS-`propertyDataType` (BIC-Gruppentoken oder echten IFC-Typnamen) einem Revit-DATATYPE zu. */
export function mapIdsDataTypeToRevit(
  dataType: string | undefined, versions: readonly string[], fallback: RevitDataType,
): RevitDataType {
  const token = (dataType ?? '').trim();
  if (!token) return fallback;

  const group = dataTypeGroupOf(token);
  if (group) return BIC_GROUP_TO_REVIT[group.token] ?? fallback;

  const override = MEASURE_NAME_OVERRIDES.find(entry => entry.pattern.test(token));
  if (override) return override.dataType;

  switch (primitiveOfDataType(token, versions)) {
    case 'string': return 'TEXT';
    case 'integer': return 'INTEGER';
    case 'real':
    case 'number': return 'NUMBER';
    case 'boolean':
    case 'logical': return 'YESNO';
    default: return fallback;
  }
}

/** Literaler Wert eines Werte-Felds, oder `null` bei Mustern/Bereichen/leeren Feldern ohne einen einzelnen Wert. */
function literalValue(field: IDSValueField, ignoreWildcard = false): string | null {
  let value: string | null = null;
  if (field.dataType === 'Wert') value = field.simpleValue?.trim() || null;
  else if (field.dataType === 'Aufzählung' && field.enumValues?.length === 1) value = field.enumValues[0]?.trim() || null;

  if (!value) return null;
  if (ignoreWildcard && (value === '.*' || value === '*')) return null;
  return value;
}

/** Entfernt Tabs/Zeilenumbrüche, die das Tab-getrennte Dateiformat sonst korrumpieren würden. */
function sanitizeCell(value: string): string {
  return value.replace(/[\t\r\n]+/g, ' ').trim();
}

interface ExportEntry {
  name: string;
  dataType: RevitDataType;
  groupName: string;
  description: string;
}

/** Sammelt exportierbare Eigenschafts-Anforderungen aus allen Spezifikationen. */
function collectEntries(
  rule: IDSRule, options: RevitExportOptions,
): { entries: ExportEntry[]; skippedInvalidFacets: number; skippedByFilter: number } {
  const entries: ExportEntry[] = [];
  let skippedInvalidFacets = 0;
  let skippedByFilter = 0;

  for (const spec of rule.specifications) {
    for (const facet of spec.requirements as IDSFacet[]) {
      if (facet.type !== 'property' || facet.metaNecessity === 'prohibited') continue;

      const propertyName = literalValue(facet.propertyName);
      if (!propertyName) {
        skippedInvalidFacets++;
        continue;
      }
      const propertySetName = literalValue(facet.propertySet, true);

      if (isExcluded(propertyName, options.excludedProperties) || isExcluded(propertySetName, options.excludedPropertySets)) {
        skippedByFilter++;
        continue;
      }

      const resolvedName = options.nameScheme === 'propertySetProperty' && propertySetName
        ? `${propertySetName}.${propertyName}`
        : propertyName;
      const groupName = options.groupBy === 'propertySet'
        ? (propertySetName || 'Ohne PropertySet')
        : options.groupBy === 'specification'
          ? (spec.name?.trim() || 'Ohne Spezifikation')
          : (rule.info.title?.trim() || 'Parameter');
      const description = [`Spezifikation „${spec.name?.trim() || 'Unbenannt'}"`, propertySetName]
        .filter(Boolean).join(' · ');

      entries.push({
        name: sanitizeCell(resolvedName),
        dataType: mapIdsDataTypeToRevit(facet.propertyDataType, spec.ifcVersions, options.fallbackDataType),
        groupName: sanitizeCell(groupName),
        description: sanitizeCell(description),
      });
    }
  }

  return {entries, skippedInvalidFacets, skippedByFilter};
}

function serialize(groups: ExistingSpGroup[], params: ExistingSpParam[]): string {
  const lines: string[] = [
    '# This is a Revit shared parameter file.',
    '# Do not edit manually.',
    '*META\tVERSION\tMINVERSION',
    'META\t2\t1',
    '*GROUP\tID\tNAME',
    ...groups.map(g => `GROUP\t${g.id}\t${g.name}`),
    '*PARAM\tGUID\tNAME\tDATATYPE\tDATACATEGORY\tGROUP\tVISIBLE\tDESCRIPTION\tUSERMODIFIABLE',
    ...params.map(p => [
      'PARAM', p.guid, p.name, p.dataType, p.dataCategory, String(p.groupId),
      p.visible ? '1' : '0', p.description, p.userModifiable ? '1' : '0',
    ].join('\t')),
  ];
  return lines.join('\r\n') + '\r\n';
}

/**
 * Baut die Shared-Parameter-Datei aus den Anforderungen der aktuell bearbeiteten IDS.
 *
 * `existing` ist das Ergebnis von {@link parseSharedParameterFile} einer hochgeladenen Altdatei -
 * oder `null`, wenn keine hochgeladen wurde (dann bekommt jeder Parameter eine neue GUID).
 */
export function buildSharedParameterFile(
  rule: IDSRule, options: RevitExportOptions, existing: ExistingSharedParameterFile | null = null,
): RevitExportResult {
  const {entries, skippedInvalidFacets, skippedByFilter} = collectEntries(rule, options);

  // Namenskollisionen: Revit erlaubt keine doppelten Parameternamen - erste Zuordnung gewinnt.
  const byName = new Map<string, ExportEntry>();
  const skippedDuplicateNames: string[] = [];
  for (const entry of entries) {
    const key = entry.name.toLowerCase();
    if (byName.has(key)) {
      skippedDuplicateNames.push(entry.name);
      continue;
    }
    byName.set(key, entry);
  }

  const groupIdByName = new Map<string, number>();
  const groupNameById = new Map<number, string>();
  let nextGroupId = 1;
  for (const g of existing?.groups ?? []) {
    groupIdByName.set(g.name.toLowerCase(), g.id);
    groupNameById.set(g.id, g.name);
    nextGroupId = Math.max(nextGroupId, g.id + 1);
  }
  const resolveGroupId = (name: string): number => {
    const key = name.toLowerCase();
    let id = groupIdByName.get(key);
    if (id == null) {
      id = nextGroupId++;
      groupIdByName.set(key, id);
      groupNameById.set(id, name);
    }
    return id;
  };

  const existingGuidByName = new Map<string, string>();
  for (const p of existing?.params ?? []) {
    existingGuidByName.set(p.name.toLowerCase(), p.guid);
  }

  const usedGroupIds = new Set<number>();
  const finalParams: ExistingSpParam[] = [];
  for (const entry of byName.values()) {
    const groupId = resolveGroupId(entry.groupName);
    usedGroupIds.add(groupId);
    finalParams.push({
      guid: existingGuidByName.get(entry.name.toLowerCase()) ?? crypto.randomUUID(),
      name: entry.name,
      dataType: entry.dataType,
      dataCategory: '',
      groupId,
      visible: true,
      description: entry.description,
      userModifiable: true,
    });
  }

  let orphanedKept = 0;
  if (options.keepOrphanedParams) {
    for (const p of existing?.params ?? []) {
      if (byName.has(p.name.toLowerCase())) continue;
      // Ein ausgeschlossener Parameter darf nicht über die Altdatei zurück in die Datei kommen,
      // sonst liefe der Filter bei jedem Re-Export ins Leere.
      if (isExcluded(p.name, options.excludedProperties)) continue;
      usedGroupIds.add(p.groupId);
      finalParams.push(p);
      orphanedKept++;
    }
  }

  const finalGroups = [...usedGroupIds]
    .map(id => ({id, name: groupNameById.get(id) ?? `Gruppe ${id}`}))
    .sort((a, b) => a.id - b.id);

  return {
    fileText: serialize(finalGroups, finalParams),
    paramCount: finalParams.length,
    groupCount: finalGroups.length,
    skippedDuplicateNames,
    skippedInvalidFacets,
    orphanedKept,
    skippedByFilter,
  };
}
